import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft, MessageCircle, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getProduct, getCategory, getProducts } from '../services/firebaseService';

const PRODUCT_PLACEHOLDER = "https://pub-2603f7a1a1604332b276f2ff8fff86f5.r2.dev/polymax/placeholder.png";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [allImages, setAllImages] = useState([]);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const productData = await getProduct(productId);
      setProduct(productData);

      const images = [];
      if (productData?.image_url) {
        images.push(productData.image_url);
      }
      if (productData?.images && productData.images.length > 0) {
        images.push(...productData.images);
      }
      if (images.length === 0) {
        images.push(PRODUCT_PLACEHOLDER);
      }
      setAllImages(images);
      setSelectedImage(0);

      if (productData?.category_id) {
        const categoryData = await getCategory(productData.category_id);
        setCategory(categoryData);

        const allProducts = await getProducts(productData.category_id);
        setRelatedProducts(allProducts.filter(p => p.id !== productId).slice(0, 4));
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1e3a5f] border-t-transparent"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <p className="text-slate-500 text-lg mb-4">Məhsul tapılmadı</p>
        <Link to="/products">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Məhsullara Qayıt
          </Button>
        </Link>
      </div>
    );
  }

  const whatsappMessage = `Salam! "${product.name}" məhsulu haqqında məlumat almaq istəyirəm.`;
  const whatsappUrl = `https://wa.me/994506555501?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="min-h-screen bg-[#f8fafc] pt-24">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center text-sm text-slate-500">
            <Link to="/" className="hover:text-[#1e3a5f]">Ana Səhifə</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <Link to="/products" className="hover:text-[#1e3a5f]">Məhsullar</Link>
            {category && (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <Link to={`/products?category=${category.id}`} className="hover:text-[#1e3a5f]">
                  {category.name}
                </Link>
              </>
            )}
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-[#1e3a5f] font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="relative rounded-2xl overflow-hidden shadow-sm">
              <img
                src={allImages[selectedImage] || PRODUCT_PLACEHOLDER}
                alt={product.name}
                className="w-full h-auto object-cover"
                onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
              />
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-md transition-all"
                  >
                    <ChevronLeft className="h-6 w-6 text-[#1e3a5f]" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-md transition-all"
                  >
                    <ChevronRight className="h-6 w-6 text-[#1e3a5f]" />
                  </button>
                </>
              )}
            </div>
            
            {allImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-[#1e3a5f]' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {category && (
              <span className="inline-block bg-[#1e3a5f]/10 text-[#1e3a5f] text-sm px-4 py-1 rounded-full">
                {category.name}
              </span>
            )}
            
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f]">
              {product.name}
            </h1>

            {product.show_price && product.price && (
              <p className="text-3xl font-bold text-[#3b82f6]">
                {product.price} AZN
              </p>
            )}

            {product.description && (
              <p className="text-slate-600 text-lg leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            )}

            {product.features && product.features.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-[#1e3a5f]">Xüsusiyyətlər:</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-slate-600">
                      <span className="w-2 h-2 bg-[#3b82f6] rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-4 pt-6">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#25d366] hover:bg-[#25d366]/90">
                  <MessageCircle className="h-5 w-5 mr-2" fill="white" />
                  WhatsApp ilə Sifariş
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
                  Əlaqə Saxla
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-20">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-8">
              Oxşar Məhsullar
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link 
                  key={relatedProduct.id} 
                  to={`/products/${relatedProduct.id}`}
                  className="group"
                >
                  <Card className="card-hover overflow-hidden">
                    <div className="aspect-square bg-slate-100 relative overflow-hidden">
                      <img
                        src={relatedProduct.image_url || PRODUCT_PLACEHOLDER}
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-[#1e3a5f] group-hover:text-[#3b82f6] transition-colors line-clamp-1">
                        {relatedProduct.name}
                      </h3>
                      {relatedProduct.show_price && relatedProduct.price && (
                        <p className="font-bold text-[#3b82f6] mt-1">{relatedProduct.price} AZN</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
